/**
 * When this device last used the app, so a return after a while away can be treated differently: the welcome
 * screen greets you after 6 hours (app/Welcome.tsx), and after 60 minutes you land on Home with your stale
 * live parties left (app/awayReset.ts).
 *
 * "In use" means the page is open and visible: the mark is refreshed every minute while visible and when the
 * page is put away. The time away is read once when the app starts (before anything refreshes the mark), and
 * again each time a hidden tab comes back.
 */
const KEY = 'gmp.lastActive';

/** Away at least this long: land on Home and leave the live parties you were in. */
export const RESET_AFTER_MS = 60 * 60 * 1000;

/**
 * Whether a return counts as "away for an hour or more". A device that has never used the app (no stamp,
 * Infinity) does not: it has no seat here, and it must not leave parties a signed-in account plays elsewhere.
 */
export const staleAfterAway = (awayMs: number) => Number.isFinite(awayMs) && awayMs >= RESET_AFTER_MS;

function read(): number | null {
  try {
    const raw = localStorage.getItem(KEY);
    const n = raw === null ? null : Number(raw);
    return n !== null && Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function markActive() {
  try {
    localStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* storage blocked */
  }
}

/** Milliseconds since the app was last used on this device, as of when it started (Infinity: never). */
export function awayFor(last: number | null, now: number): number {
  if (last === null || last > now + 60_000) return Infinity;
  return Math.max(0, now - last);
}

const started = typeof window !== 'undefined';
export const awayAtStart: number = started ? awayFor(read(), Date.now()) : 0;

const returnListeners = new Set<(awayMs: number) => void>();
/** Calls `fn` with the time away whenever a hidden page becomes visible again. */
export function onReturn(fn: (awayMs: number) => void): () => void {
  returnListeners.add(fn);
  return () => void returnListeners.delete(fn);
}

if (started) {
  markActive();
  let hiddenAt: number | null = document.visibilityState === 'hidden' ? Date.now() : null;
  setInterval(() => document.visibilityState === 'visible' && markActive(), 60_000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      markActive();
    } else {
      const last = read();
      // The page may have been frozen while hidden: take the longer of the two measures of time away.
      const away = Math.max(hiddenAt === null ? 0 : Date.now() - hiddenAt, last === null ? 0 : Date.now() - last);
      hiddenAt = null;
      markActive();
      for (const fn of returnListeners) fn(away);
    }
  });
  window.addEventListener('pagehide', markActive);
}
